import { type PropsWithChildren, useEffect, useMemo } from 'react';
import { App as AntdApp, ConfigProvider, theme as antdTheme } from 'antd';
import { useThemeStore } from '@presentation/stores/themeStore';

/**
 * Mirrors the `--color-brand-*` scale in `presentation/styles/index.css`.
 * Kept as literal hex here (not a `var(--color-brand-600)` reference)
 * because Ant Design's algorithm derives hover/active shades by computing
 * against the literal color value — an unresolved CSS variable would break
 * that derivation. If the brand palette changes, update both places.
 */
const DEFAULT_COLORS = {
  dark: {
    accent: '#3466FF',
    background: '#0B0F19',
    surface: '#111827',
    border: '#1F2937',
    text: '#E5E7EB',
  },
  light: {
    accent: '#1C46F5',
    background: '#FFFFFF',
    surface: '#F3F4F6',
    border: '#E5E7EB',
    text: '#111827',
  },
};

/**
 * Bridges our app's theme store (mode + optional selected theme colors,
 * driven by the backend `themes` catalog) into Ant Design's token system.
 * Tailwind is still used for layout/spacing; every interactive component
 * renders through Ant Design per the design system requirements.
 */
export const AntdThemeProvider = ({ children }: PropsWithChildren) => {
  const mode = useThemeStore((state) => state.mode);
  const colors = DEFAULT_COLORS[mode];

  // Pages outside antd's own component tree (e.g. the auth pages' plain
  // full-viewport <div> wrappers) never receive antd's generated CSS
  // tokens — only components antd itself renders do. Without this, toggling
  // theme repaints every antd component but leaves the raw page background
  // white, since nothing else was painting it. This keeps that page canvas
  // in sync with the same palette passed to ConfigProvider below.
  useEffect(() => {
    document.body.style.backgroundColor = colors.background;
    document.body.style.color = colors.text;
  }, [colors]);

  const themeConfig = useMemo(
    () => ({
      algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary: colors.accent,
        colorBgContainer: colors.surface,
        colorBgLayout: colors.background,
        colorBorder: colors.border,
        borderRadius: 8,
        fontFamily: 'inherit',
      },
      components: {
        // Layout.Header/Sider default to a fixed dark navy regardless of
        // algorithm (antd's classic "admin dashboard" look) — without this
        // override the header stays dark even when the rest of the app
        // switches to light mode.
        Layout: {
          headerBg: colors.surface,
          bodyBg: colors.background,
          siderBg: colors.surface,
        },
      },
    }),
    [mode, colors],
  );

  return (
    <ConfigProvider theme={themeConfig}>
      <AntdApp>{children}</AntdApp>
    </ConfigProvider>
  );
};
